import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import {
  ArrowRight, Play, Hammer, Building2, HandCoins, TrendingUp,
  Key, Users, ShieldCheck, Leaf, Phone, ArrowUpRight, Instagram, Youtube, Linkedin,
} from "lucide-react";

const FEATURES = [
  { icon: Key, title: "Anahtar Teslim", desc: "Peşin, zamanında ve eksiksiz teslim garantisi." },
  { icon: Users, title: "Müşteri Odaklı Yaklaşım", desc: "İhtiyaçlarınıza özel çözümler üretiyoruz." },
  { icon: ShieldCheck, title: "Kalite ve Güven", desc: "En yüksek kalite standartlarında, güvenli yapılar." },
  { icon: Leaf, title: "Sürdürülebilir Gelecek", desc: "Çevreye duyarlı, sürdürülebilir projeler geliştiriyoruz." },
];

const SERVICE_ICONS = { hammer: Hammer, building: Building2, "hand-coins": HandCoins, "trending-up": TrendingUp };

export default function Home() {
  useSEO({
    title: "Ana Sayfa — Değer Üretiyoruz",
    description: "YK İnşaat Müteahhitlik — 25+ yıllık tecrübe. Modern konut, villa ve ticari projeler. Anahtar teslim müteahhitlik.",
    path: "/",
  });
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/projects").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const heroImage = "https://images.pexels.com/photos/24805054/pexels-photo-24805054.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
  const cityscape = "https://images.unsplash.com/photo-1579847188804-ecba0e2ea330?crop=entropy&cs=srgb&fm=jpg&w=900&q=80";

  const getImg = (p) => (p?.startsWith("http") ? p : fileUrl(p));

  return (
    <div className="bg-[#0A0E1A]" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="YK İnşaat" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E1A] via-[#0A0E1A]/85 to-[#0A0E1A]/20"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E1A]/60 via-transparent to-[#0A0E1A]"></div>
        </div>

        {/* Vertical social */}
        <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/60 [writing-mode:vertical-rl] rotate-180">Bizi Takip Edin</div>
          <div className="w-px h-16 bg-[#C9A961]/40"></div>
          <div className="flex flex-col gap-4">
            <a href="#" className="text-white/60 hover:text-[#C9A961] transition-colors"><Instagram className="w-4 h-4" /></a>
            <a href="#" className="text-white/60 hover:text-[#C9A961] transition-colors"><Linkedin className="w-4 h-4" /></a>
            <a href="#" className="text-white/60 hover:text-[#C9A961] transition-colors"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>

        {/* Experience badge */}
        <div className="hidden md:block absolute top-32 right-8 lg:right-20 z-10">
          <div className="bg-[#111827]/80 backdrop-blur-md border border-[#C9A961]/30 px-6 py-6 text-center">
            <div className="text-4xl font-heading text-[#C9A961] leading-none">25+</div>
            <div className="w-8 h-px bg-[#C9A961] mx-auto my-3"></div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/80">Yıllık<br />Tecrübe</div>
          </div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-24 pt-24 lg:pt-32 pb-16 z-10">
          <div className="section-label mb-6 animate-fade-up">Geleceğiniz için</div>
          <h1
            data-testid="hero-title"
            className="font-heading text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-[0.02em] animate-fade-up"
            style={{ animationDelay: "0.1s" }}
          >
            {stats?.hero_title_line1 || "DEĞER ÜRETİYORUZ"}
            <span className="block text-[#C9A961] mt-3">{stats?.hero_title_line2 || "HAYALLERİ İNŞA EDİYORUZ"}</span>
          </h1>
          <p
            className="mt-8 max-w-xl text-white/70 text-base lg:text-lg leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            {stats?.hero_subtitle || "YK İnşaat Müteahhitlik, modern yaşam alanları inşa eder, sürdürülebilir yatırımlarla geleceğe değer katar."}
          </p>

          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/projeler" data-testid="hero-cta-projects" className="btn-gold group">
              Projelerimizi Keşfedin
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/iletisim" data-testid="hero-cta-contact" className="btn-gold-outline group">
              Bize Ulaşın
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services strip */}
      <section className="relative -mt-4 z-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="bg-[#0F1524]/95 backdrop-blur-lg border border-[#1F2937] grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2937]">
            {services.slice(0, 4).map((s) => {
              const Icon = SERVICE_ICONS[s.icon] || Hammer;
              return (
                <div key={s.id} className="bg-[#0F1524] p-8 hover:bg-[#111827] transition-colors group">
                  <Icon className="w-9 h-9 text-[#C9A961] mb-4" strokeWidth={1.2} />
                  <div className="text-[#C9A961] uppercase tracking-[0.2em] text-xs font-semibold mb-3">{s.title}</div>
                  <div className="text-white/60 text-sm leading-relaxed">{s.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats block */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1">
              <div className="section-label mb-6">Rakamlarla YK İnşaat</div>
              <h2 className="font-heading text-3xl lg:text-4xl text-white leading-tight tracking-[0.05em]">
                Güçlü Temeller<br />
                <span className="text-[#C9A961]">Sağlam Gelecek</span>
              </h2>
              <Link to="/projeler" data-testid="stats-all-projects" className="mt-8 btn-gold-outline text-xs inline-flex">
                Tüm Projeler <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937]">
              {[
                { icon: Building2, value: stats?.projects || "120+", label: "Tamamlanan\nProje" },
                { icon: Hammer, value: stats?.area || "2.5M m²", label: "İnşaat\nAlanı" },
                { icon: Users, value: stats?.families || "850+", label: "Mutlu\nAile" },
                { icon: TrendingUp, value: stats?.experience || "25+", label: "Yıllık\nTecrübe" },
              ].map((s, i) => (
                <div key={i} className="bg-[#0F1524] p-8 text-center">
                  <s.icon className="w-8 h-8 text-[#C9A961] mx-auto mb-4" strokeWidth={1.2} />
                  <div className="font-heading text-4xl lg:text-5xl text-[#C9A961]">{s.value}</div>
                  <div className="mt-3 text-white/60 uppercase text-[10px] tracking-[0.25em] whitespace-pre-line leading-relaxed">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-16 border-y border-[#1F2937]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES.map((f, i) => (
            <div key={i} className="group">
              <f.icon className="w-10 h-10 text-[#C9A961] mb-5" strokeWidth={1.2} />
              <div className="text-[#C9A961] uppercase tracking-[0.2em] text-xs font-semibold mb-3">{f.title}</div>
              <div className="text-white/60 text-sm leading-relaxed">{f.desc}</div>
              <div className="mt-6 w-8 h-px bg-[#C9A961]/50 group-hover:w-full group-hover:bg-[#C9A961] transition-all duration-500"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured projects */}
      {featured.length > 0 && (
        <section className="py-24 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div>
                <div className="section-label mb-4">Öne Çıkan Projeler</div>
                <h2 className="font-heading text-3xl lg:text-5xl text-white tracking-[0.05em] leading-tight">
                  Her Projemizde<br />
                  <span className="text-[#C9A961]">İmzamız Vardır</span>
                </h2>
              </div>
              <Link to="/projeler" className="btn-gold-outline text-xs" data-testid="featured-view-all">
                Tümünü Gör <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((p) => (
                <Link
                  key={p.id}
                  to={`/projeler/${p.id}`}
                  data-testid={`featured-project-${p.id}`}
                  className="group relative overflow-hidden yk-card"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#050812]">
                    {p.images?.[0] ? (
                      <img
                        src={getImg(p.images[0])}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#111827] flex items-center justify-center text-[#1F2937]">
                        <Building2 className="w-16 h-16" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A] via-[#0A0E1A]/30 to-transparent"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="text-[#C9A961] text-[10px] uppercase tracking-[0.3em] mb-2">{p.category} • {p.year}</div>
                    <div className="font-heading text-white text-xl tracking-wider mb-2 group-hover:text-[#C9A961] transition-colors">{p.title}</div>
                    <div className="text-white/60 text-sm">{p.location}</div>
                    <div className="mt-4 flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-widest">
                      Detay <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={cityscape} alt="Şehir" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E1A] via-[#0A0E1A]/90 to-[#0A0E1A]/50"></div>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="section-label mb-4">Hayaliniz Bizim İşimiz</div>
            <h2 className="font-heading text-3xl lg:text-5xl text-white tracking-[0.03em] leading-tight">
              Hayalinizdeki<br />
              <span className="text-[#C9A961]">Yaşam Alanını Birlikte İnşa Edelim</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-lg leading-relaxed">
              Uzman ekibimiz ve 25 yılı aşkın tecrübemizle projenizi hayata geçirmek için buradayız.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/iletisim" className="btn-gold">Proje Geliştirme <ArrowRight className="w-4 h-4" /></Link>
              <a href="tel:05449751294" className="inline-flex items-center gap-3 text-white hover:text-[#C9A961] transition-colors">
                <div className="w-12 h-12 rounded-full border border-[#C9A961] flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#C9A961]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Hemen Arayın</div>
                  <div className="text-lg font-semibold tracking-wider">0544 975 12 94</div>
                </div>
              </a>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="relative aspect-video bg-[#111827] border border-[#C9A961]/30 group cursor-pointer overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1564995408557-f12ea1e4ecf1?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                alt="Tanıtım"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-[#0A0E1A]/40 group-hover:bg-[#0A0E1A]/20 transition-colors flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-[#C9A961] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-[#0A0E1A] ml-1" fill="#0A0E1A" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 text-white/80 uppercase text-xs tracking-[0.3em]">Tanıtım Filmini İzleyin</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
